import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonService } from 'src/app/provide/common.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ApiEndPoint } from 'src/app/config/api-end-point';

@Component({
    selector: 'app-privacy-policy',
    templateUrl: './privacy-policy.component.html',
    styleUrls: ['./privacy-policy.component.scss'],
    standalone: false
})
export class PrivacyPolicyComponent implements OnInit {
  pageName: any;
  description: any;
  type: any;
  result: any;
  staticContentDetails: any;
  privacyData: any;
  isLoading: boolean = true;
  documentOnly: boolean = false;
  sanitizedPdfUrl: SafeResourceUrl | null = null;

  constructor(
    private fb: UntypedFormBuilder,
    public commonService: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    window.scrollTo(0, 0);
    
    // Check if this is a document-only view
    this.route.data.subscribe(data => {
      this.documentOnly = data.documentOnly === true;
    });
    
    this.getPrivacy();
  }

  getPrivacy() {
    this.isLoading = true;
    this.commonService.showSpinner();
    this.commonService.getApi(ApiEndPoint.privacyPolicies, 0).subscribe(
      (res: any) => {
        if (res['responseCode'] == 200) {
          this.privacyData = res['result'];
          this.setDocumentTypeFlags();
          this.commonService.hideSpinner();
          this.isLoading = false;
        } else {
          this.commonService.hideSpinner();
          this.isLoading = false;
          this.commonService.errorToast(res.responseMessage);
        }
      }, 
      err => {
        this.commonService.hideSpinner();
        this.isLoading = false;
        this.commonService.warningToast(err['error']['responseMessage']);
      }
    );
  }

  setDocumentTypeFlags() {
    const url = this.privacyData?.documentUrl?.toLowerCase() || '';
    console.log('DEBUG: Document URL:', this.privacyData?.documentUrl);
    console.log('DEBUG: isSupportedDocument:', this.isSupportedDocument());
    console.log('DEBUG: isOfficeDocument:', this.isOfficeDocument());
    console.log('DEBUG: isImageDocument:', this.isImageDocument());
    console.log('DEBUG: isTxtDocument:', this.isTxtDocument());
    // FORCE PDF logic for all documents for testing
    let pdfUrl = this.privacyData.documentUrl;
    if (pdfUrl && pdfUrl.endsWith('.pdf') && !pdfUrl.includes('#')) {
      pdfUrl += '#view=FitH';
    }
    this.sanitizedPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
  }

  isSupportedDocument(): boolean {
    if (!this.privacyData || !this.privacyData.documentUrl) return false;
    const url = this.privacyData.documentUrl.toLowerCase();
    const documentType = this.privacyData.documentType ? this.privacyData.documentType.toLowerCase() : '';
    return url.endsWith('.pdf') || documentType.includes('pdf');
  }

  isImageDocument(): boolean {
    if (!this.privacyData || !this.privacyData.documentUrl) return false;
    const url = this.privacyData.documentUrl.toLowerCase();
    return ['.jpg', '.jpeg', '.png', '.gif'].some(ext => url.endsWith(ext));
  }

  isTxtDocument(): boolean {
    if (!this.privacyData || !this.privacyData.documentUrl) return false;
    const url = this.privacyData.documentUrl.toLowerCase();
    return url.endsWith('.txt');
  }

  isOtherDocument(): boolean {
    if (!this.privacyData || !this.privacyData.documentUrl) return false;
    const url = this.privacyData.documentUrl.toLowerCase();
    return !this.isSupportedDocument() && !this.isImageDocument() && !this.isOfficeDocument() && !this.isTxtDocument();
  }

  // Helper method to check if a URL is from S3
  private isS3Url(url: string): boolean {
    if (!url) return false;
    
    return url.includes('amazonaws.com') || 
           url.includes('s3.') || 
           url.includes('s3-');
  }

  // Determine if the document is an Office document (Word, Excel, PowerPoint)
  isOfficeDocument(): boolean {
    if (!this.privacyData || !this.privacyData.documentUrl) return false;
    
    const url = this.privacyData.documentUrl.toLowerCase();
    const officeExtensions = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
    
    return officeExtensions.some(ext => url.endsWith(ext)) || 
           (this.privacyData.documentType && 
            (this.privacyData.documentType.toLowerCase().includes('word') || 
             this.privacyData.documentType.toLowerCase().includes('excel') || 
             this.privacyData.documentType.toLowerCase().includes('powerpoint') ||
             this.privacyData.documentType.toLowerCase().includes('office')));
  }
  
  // New getter to provide safe URL for Office documents
  get safeOfficeDocumentUrl(): SafeResourceUrl {
    if (this.privacyData && this.privacyData.documentUrl) {
      const encodedUrl = encodeURIComponent(this.privacyData.documentUrl);
      return this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`
      );
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl('');
  }

  // Close the document view and return to the regular view
  closeDocumentView(): void {
    if (window.opener) {
      window.close(); // Close the tab if opened in a new tab
    } else {
      this.router.navigate(['/privacy-policy']); // Navigate back to regular view
    }
  }

  openPdfInNewTab() {
    if (this.privacyData?.documentUrl) {
      window.open(this.privacyData.documentUrl, '_blank');
    }
  }
}
